// src/components/Wishlist.tsx
import React, { useState } from 'react';
import { X, ShoppingCart } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { toast } from 'react-hot-toast';

// -- Type Definitions --
interface ProductImage {
  url: string;
}

interface ProductVariations {
  color?: string;
  size?: string;
  ageRange?: string;
}

export interface Product {
  id: number;
  name: string;
  price: number;
  images?: ProductImage[];
  // Either already parsed or available in variations_config.
  variations?: {
    type: string;
    colors: string[];
    sizes: string[];
    ageRanges: string[];
    stockByVariation: any[];
  };
  variations_config?: string;
  selectedVariations?: ProductVariations;
}

export interface WishlistItem extends Product {
  quantity: number;
}

interface WishlistProps {
  onClose: () => void;
}

// -- Component Implementation --
export const Wishlist: React.FC<WishlistProps> = ({ onClose }) => {
  const { state, dispatch } = useApp();
  const backendBaseURL = import.meta.env.VITE_API_BASE_URL;
  const backendBaseImagesURL = import.meta.env.VITE_API_BASE_IMAGES_URL;

  // Helper to build full image URL
  const getFullImageURL = (url: string): string => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${backendBaseImagesURL}${url}`;
  };

  // Local state for controlling the Variation Modal when moving an item to cart.
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [modalItem, setModalItem] = useState<Product | null>(null);

  // Normalizes a product’s variations.
  const normalizeVariations = (product: Product) => {
    return product.variations ||
      (product.variations_config ? JSON.parse(product.variations_config) : { type: 'none', colors: [], sizes: [], ageRanges: [], stockByVariation: [] });
  };

  // Remove item from wishlist. We dispatch TOGGLE_WISHLIST with the full product.
  const removeFromWishlist = (item: Product) => {
    dispatch({ type: 'TOGGLE_WISHLIST', payload: item });
    toast.success('Item removed from wishlist');
  };

  // Move item to cart. If the product requires variation selection, open the modal.
  const moveToCart = (product: Product) => {
    const normalized = normalizeVariations(product);
    if (normalized.type && normalized.type !== 'none') {
      // Open modal for variation selection.
      setModalItem(product);
      setShowVariationModal(true);
    } else {
      // Simple product—move directly.
      const cartItem: WishlistItem = {
        ...product,
        quantity: 1,
        selectedVariations: product.selectedVariations || {},
        price: Number(product.price) || 0,
      };
      dispatch({ type: 'ADD_TO_CART', payload: cartItem });
      dispatch({ type: 'TOGGLE_WISHLIST', payload: product });
      toast.success('Item moved to cart');
    }
  };

  const formatPrice = (price: number | string | undefined): string => {
    const numPrice = Number(price);
    return !isNaN(numPrice) ? numPrice.toLocaleString() : '0';
  };

  // ---------------- Variation Modal Component ----------------
  interface VariationModalProps {
    product: Product;
    variations: {
      type: string;
      colors: string[];
      sizes: string[];
      ageRanges: string[];
      stockByVariation: any[];
    };
    onConfirm: (selected: ProductVariations) => void;
    onClose: () => void;
  }

  const VariationModal: React.FC<VariationModalProps> = ({ product, variations, onConfirm, onClose }) => {
    const [localSelection, setLocalSelection] = useState<ProductVariations>({});

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white p-6 rounded-lg w-80">
          <h2 className="text-xl font-bold mb-4">Select Options for {product.name}</h2>

          {variations.colors && variations.colors.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium">Color</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {variations.colors.map((color: string) => (
                  <button
                    key={color}
                    onClick={() => setLocalSelection(prev => ({ ...prev, color }))}
                    className={`px-4 py-2 rounded border ${
                      localSelection.color === color
                        ? 'border-pink-500 bg-pink-50 text-pink-500'
                        : 'border-gray-300 hover:border-pink-500'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variations.sizes && variations.sizes.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium">Size</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {variations.sizes.map((size: string) => (
                  <button
                    key={size}
                    onClick={() => setLocalSelection(prev => ({ ...prev, size }))}
                    className={`px-4 py-2 rounded border ${
                      localSelection.size === size
                        ? 'border-pink-500 bg-pink-50 text-pink-500'
                        : 'border-gray-300 hover:border-pink-500'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variations.ageRanges && variations.ageRanges.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium">Age Range</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {variations.ageRanges.map((age: string) => (
                  <button
                    key={age}
                    onClick={() => setLocalSelection(prev => ({ ...prev, ageRange: age }))}
                    className={`px-4 py-2 rounded border ${
                      localSelection.ageRange === age
                        ? 'border-pink-500 bg-pink-50 text-pink-500'
                        : 'border-gray-300 hover:border-pink-500'
                    }`}
                  >
                    {age}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (variations.colors && variations.colors.length > 0 && !localSelection.color) {
                  toast.error('Please select a color');
                  return;
                }
                if (variations.sizes && variations.sizes.length > 0 && !localSelection.size) {
                  toast.error('Please select a size');
                  return;
                }
                if (variations.ageRanges && variations.ageRanges.length > 0 && !localSelection.ageRange) {
                  toast.error('Please select an age range');
                  return;
                }
                onConfirm(localSelection);
              }}
              className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
            >
              Move to Cart
            </button>
          </div>
        </div>
      </div>
    );
  };
  // ---------------- End of Variation Modal ----------------

  if (state.wishlist.length === 0) {
    return (
      <div className="fixed right-0 top-16 w-96 h-[calc(100vh-4rem)] bg-white shadow-xl p-6 transform transition-transform z-50">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Wishlist</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-gray-500">Your wishlist is empty</p>
      </div>
    );
  }

  return (
    <>
      <div className="fixed right-0 top-16 w-96 h-[calc(100vh-4rem)] bg-white shadow-xl p-6 transform transition-transform z-50 overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Wishlist</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4">
          {state.wishlist.map((item) => {
            // Ensure the item is normalized as a Product
            const wishlistItem: Product = {
              ...item,
              price: Number(item.price) || 0,
            };
            return (
              <div key={wishlistItem.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <img
                  src={
                    wishlistItem.images && wishlistItem.images.length > 0 
                      ? getFullImageURL(wishlistItem.images[0].url) 
                      : '/path/to/default-image.jpg'
                  }
                  alt={wishlistItem.name}
                  className="w-20 h-20 object-cover rounded-md"
                />
                <div className="flex-1">
                  <h3 className="font-semibold">{wishlistItem.name}</h3>
                  <p className="text-pink-500 font-bold">KES {formatPrice(wishlistItem.price)}</p>
                  {wishlistItem.selectedVariations && Object.keys(wishlistItem.selectedVariations).length > 0 && (
                    <div className="text-sm text-gray-600 mt-1">
                      {wishlistItem.selectedVariations.color && (
                        <span className="mr-2">Color: {wishlistItem.selectedVariations.color}</span>
                      )}
                      {wishlistItem.selectedVariations.size && (
                        <span className="mr-2">Size: {wishlistItem.selectedVariations.size}</span>
                      )}
                      {wishlistItem.selectedVariations.ageRange && (
                        <span>Age: {wishlistItem.selectedVariations.ageRange}</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => moveToCart(wishlistItem)}
                      className="flex items-center gap-1 px-3 py-1 bg-pink-500 text-white rounded-full text-sm hover:bg-pink-600 transition-colors"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      Move to Cart
                    </button>
                    <button
                      onClick={() => removeFromWishlist(wishlistItem)}
                      className="ml-auto p-1 rounded-full hover:bg-gray-200 text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showVariationModal && modalItem && (
        <VariationModal
          product={modalItem}
          variations={normalizeVariations(modalItem)}
          onConfirm={(selected) => {
            const cartItem: WishlistItem = {
              ...modalItem,
              quantity: 1,
              selectedVariations: selected,
              price: Number(modalItem.price) || 0,
            };
            dispatch({ type: 'ADD_TO_CART', payload: cartItem });
            dispatch({ type: 'TOGGLE_WISHLIST', payload: modalItem });
            setShowVariationModal(false);
            setModalItem(null);
            toast.success('Item moved to cart');
          }}
          onClose={() => {
            setShowVariationModal(false);
            setModalItem(null);
          }}
        />
      )}
    </>
  );
};
